import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../ui/SEOHead';
import HeroCarousel from '../sections/HeroCarousel';
import ServicesGrid from '../sections/ServicesGrid';
import TestimonialsSection from '../sections/TestimonialsSection';
import AppointmentForm from '../ui/AppointmentForm';
import useScrollReveal from '../../hooks/useScrollReveal';
import { PRACTICE_INFO, DOCTORS_DATA, OFFICE_HOURS } from '../../data/navigation';
import { SCHEMA_DENTAL_PRACTICE } from '../../utils/seo';
import s from './HomePage.module.css';

export default function HomePage() {
  useScrollReveal();
  return (
    <>
      <SEOHead schema={SCHEMA_DENTAL_PRACTICE} />
      <HeroCarousel />

      {/* Trust Bar */}
      <div className={s.trustBar}>
        <div className="container">
          <div className={s.trustInner}>
            {[
              { icon: '⭐', strong: '5.0 Rating', span: '619 Google Reviews' },
              { icon: '👩‍⚕️', strong: '20+ Years', span: 'Combined Experience' },
              { icon: '🏥', strong: 'Special Care', span: 'Cosmetic · Oral Surgery · More' },
              { icon: '🦷', strong: 'All Ages', span: 'Family & General Dentistry' },
              { icon: '🔬', strong: 'Advanced Tech', span: 'GentleWave · KöR · Digital' },
            ].map((item, i) => (
              <div key={i} className={`${s.trustItem} reveal reveal-delay-${i}`}>
                <div className={s.trustIcon}>{item.icon}</div>
                <div className={s.trustText}>
                  <strong>{item.strong}</strong>
                  <span>{item.span}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Welcome */}
      <section className={`section ${s.welcome}`}>
        <div className="container">
          <div className={s.welcomeGrid}>
            <div className={`${s.welcomeText} reveal`}>
              <p className="eyebrow">Welcome</p>
              <h2 className="display-heading" style={{marginBottom:'20px'}}>A Practice Built<br/>Around <em>You</em></h2>
              <p style={{fontSize:'17px',fontWeight:'300',lineHeight:'1.8',color:'var(--slate-600)',marginBottom:'14px'}}>
                We are a patient-centered practice specializing in general, family, and special care dentistry. You can count on us to provide the best care possible using the best materials and technology, in a safe and friendly environment.
              </p>
              <p style={{fontSize:'15px',fontWeight:'300',lineHeight:'1.75',color:'var(--slate-600)'}}>
                Located at 1811 Bethlehem Pike,Ste B235, Flourtown, PA 19031
              </p>
              <div className={s.welcomeCards}>
                {[
                  { icon: '📍', title: 'Friendly Clinic Near You', text: 'Easily accessible on 1811 Bethlehem Pike,Ste B235, Flourtown, PA 19031' },
                  { icon: '🏆', title: 'Experienced Dentists', text: '17+ combined years. Patient comfort and autonomy always come first.' },
                  { icon: '❤️', title: 'Special Care', text: 'Expert care for patients with autism, dementia, Down Syndrome & more.' },
                  { icon: '🔬', title: 'Advanced Technology', text: 'GentleWave, KöR Whitening, digital imaging, and Orthosnap aligners.' },
                ].map((c, i) => (
                  <div key={i} className={s.welcomeCard}>
                    <div className={s.welcomeCardIcon}>{c.icon}</div>
                    <h4>{c.title}</h4>
                    <p>{c.text}</p>
                  </div>
                ))}
              </div>
              <div style={{marginTop:'32px',display:'flex',gap:'14px',flexWrap:'wrap'}}>
                <Link to="/book-appointment" className="btn btn-primary btn-primary-lg">Book Appointment</Link>
                <a href={PRACTICE_INFO.mapUrl} target="_blank" rel="noreferrer" className="btn btn-outline">View on Google Map ↗</a>
              </div>
            </div>
            <div className={`${s.welcomePhotos} reveal reveal-delay-2`}>
              <div className={s.photoMain}><img src="/images/practice_section1.jpg" alt="Celebrity Smile Studio" /></div>
              <div className={s.photoSecondary}><img src="/images/practice_section2.png" alt="Celebrity Smile Studio" /></div>
              <div className={s.photoBadge}><div className={s.badgeNum}>5★</div><div className={s.badgeText}>Rated Excellent</div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className={`section ${s.values}`}>
        <div className="container">
          <div className={`${s.valuesHeader} reveal`}>
            <p className="eyebrow" style={{justifyContent:'center'}}>Our Philosophy</p>
            <h1 className="" style={{textAlign:'center',marginBottom:'14px'}}>We care about your safety while you are with us</h1>
            <p style={{textAlign:'center',fontSize:'15px',fontWeight:'300',color:'var(--slate-600)'}}>We have added Air Protection System by Air Rover Inc.,
to keep indoor air clean and safe to breathe</p>
          </div>
          <div className={s.valuesGrid}>
            {[
              { icon: '🌱', title: 'Increased Air Flow', text: 'The quicker the air can be replaced per hour, the safer it is. This system quickly removes contaminants and replenishes the space with fresh, sanitized air as fast as every 23 minutes' },
              { icon: '📚', title: 'True 12-inch HEPA Filtration', text: 'Captures 99.97% of 0.3-micron dangerous particles and prevents them from being recirculated in the indoor air' },
              { icon: '🤝', title: 'Ultraviolet Germicidal Irradiation (UVGI)', text: 'Our Ultraviolet-C Spectrum Lighting (UVC) lighting is 100% safe and kills up to 99.9999% airborne viruses, bacteria, mold, and other pathogens' },
              { icon: '⚡', title: 'Negative Air Capability', text: 'Where applied, Negative Air Pressure with up to 12 Air Changes per Hour (ACH) provides the medical-grade air quality of a hospital isolation room' },
              { icon: '⚡', title: 'Carbon Filtration', text: 'Removes harmful gases, chemicals, odors, and other volatile organic compounds (VOCs)' },
              { icon: '⚡', title: 'Safety Certified and Effective', text: 'The Air Rover Air Protection System is ETL Certified and performs consistent with CDC, OSHA and ASHRAE guidelines' },
            ].map((v, i) => (
              <div key={i} className={`${s.valueCard} reveal reveal-delay-${i}`}>
                <div className={s.valueIcon}>{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicesGrid limit={8} />

      {/* Book Band */}
      <div className={s.bookBand}>
        <div className="container">
          <div className={s.bookBandInner}>
            <div className={`${s.bookBandText} reveal`}>
              <h2>Ready to Schedule Your<br/><em>Next Visit?</em></h2>
              <p>Same-day emergency appointments available. New patients always welcome.</p>
            </div>
            <div className={`${s.bookBandActions} reveal reveal-delay-2`}>
              <div className={s.phone}>
                <div className={s.phoneIcon}>📞</div>
                <div>
                  <div className={s.phoneLabel}>Call Us</div>
                  <a href={`tel:${PRACTICE_INFO.phoneRaw}`} className={s.phoneNum}>{PRACTICE_INFO.phone}</a>
                </div>
              </div>
              <Link to="/book-appointment" className="btn btn-white btn-primary-lg">Book Online →</Link>
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section className={`section ${s.about}`}>
        <div className="container">
          <div className={s.aboutGrid}>
            <div className={`reveal`}>
              <p className="eyebrow">Meet the Dentists</p>
              <h2 className="display-heading" style={{marginBottom:'20px'}}>Committed to<br/>Dental <em>Excellence</em></h2>
              <p style={{fontSize:'17px',fontWeight:'300',lineHeight:'1.8',color:'var(--slate-600)',marginBottom:'14px'}}>
                Visit Celebrity Smile Studio when in need of an experienced dentist in Flourtown, PA. Our dentists make your every Visit a comfortable and pleasant experience. Dr. Mehta, Dr. Nolf, Dr. Patel, Dr. Sopinsky and Dr. Kaushal Shah are local dentists who look after your oral health as if you’re our family. They have been practicing dentistry in the Flourtown area and have a combined experience of over 30 years.
              </p>
              <div className={s.doctorCards}>
                {DOCTORS_DATA.map(d => (
                  <Link key={d.slug} to={`/about/${d.slug}`} className={s.doctorCard}>
                    <div className={s.doctorAvatar}>{d.avatar}</div>
                    <div className={s.doctorInfo}>
                      <h4>{d.name}, {d.credentials}</h4>
                      <p>{d.specialty}</p>
                    </div>
                    <span className={s.doctorBadge}>{d.badge}</span>
                  </Link>
                ))}
              </div>
              <div style={{marginTop:'28px',display:'flex',gap:'14px',flexWrap:'wrap'}}>
                <Link to="/doctors" className="btn btn-primary">Meet Our Doctors</Link>
                <Link to="/book-appointment" className="btn btn-outline">Book a Visit</Link>
              </div>
            </div>
            <div className={`${s.aboutVisual} reveal reveal-delay-2`}>
              <div className={s.aboutImg}><img src="/images/doctor_section.png" alt="Celebrity Smile Studio" /></div>
             {/* <div className={s.aboutCard}>
                <div className={s.aboutCardLabel}>Patient Review</div>
                <div className={s.aboutCardStars}>★★★★★</div>
                <p className={s.aboutCardText}>"The best dental experience I've ever had. Dr. Tiffany and Dr. Trisha are absolute pros at making you feel comfortable."</p>
                <div className={s.aboutCardAuthor}>— Adam V., Google Review</div>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Membership 
      <section className={s.membership}>
        <div className="container">
          <div className={`${s.membershipInner} reveal`}>
            <div>
              <p className="eyebrow" style={{color:'var(--teal-200)'}}>No Insurance?</p>
              <h2 style={{fontFamily:'var(--font-display)',fontSize:'clamp(30px,3.5vw,44px)',fontWeight:'500',color:'var(--white)',lineHeight:'1.1',marginBottom:'12px'}}>Join Our <em style={{fontStyle:'italic',color:'var(--teal-200)'}}>Membership Plan</em></h2>
              <p style={{fontSize:'15px',fontWeight:'300',color:'rgba(255,255,255,.65)',lineHeight:'1.75',maxWidth:'520px',marginBottom:'28px'}}>Don't let the lack of dental insurance stand in the way of a healthy smile. Our in-house membership plan gives you access to quality care at a transparent, affordable rate.</p>
              <Link to="/patient-info/membership" className="btn btn-white btn-primary-lg">Learn More →</Link>
            </div>
            <div className={s.memberCard}>
              <div style={{fontSize:'36px',marginBottom:'12px'}}>🦷</div>
              <h3>Membership Plan</h3>
              <p>Exams, cleanings, X-rays &amp; significant discounts on all treatments — one simple annual fee.</p>
              <Link to="/patient-info/membership" className="btn btn-primary" style={{width:'100%',justifyContent:'center',marginTop:'16px'}}>Get Started</Link>
            </div>
          </div>
        </div>
      </section>*/}

            {/* ── Eagles Partnership Section ───────────────────────────── */}
      <section className={s.eagles}>
        {/* Background layer: dark field with subtle texture */}
        <div className={s.eaglesBg} aria-hidden="true">
          <div className={s.eaglesField} />
          <div className={s.eaglesGlow} />
        </div>

        <div className="container" style={{position:'relative',zIndex:2}}>
          <div className={`${s.eaglesInner} reveal`}>

            {/* Left: Logo + badge */}
            <div className={s.eaglesLogoCol}>
              {/* SVG Eagles wingmark in midnight green + silver 
              <div className={s.eaglesEmblem}>
                <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className={s.eaglesSvg}>
                  {/* Outer circle 
                  <circle cx="100" cy="100" r="96" fill="#282a74" stroke="#A5ACAF" strokeWidth="4"/>
                  {/* Inner ring 
                  <circle cx="100" cy="100" r="80" fill="none" stroke="#A5ACAF" strokeWidth="1.5" opacity="0.4"/>
                  {/* Eagle wing shape – simplified stylised wing 
                  <path d="M30 120 C50 80, 80 60, 100 55 C120 60, 150 80, 170 120 C150 110, 130 105, 100 108 C70 105, 50 110, 30 120Z" fill="#A5ACAF"/>
                  <path d="M40 128 C60 100, 82 86, 100 82 C118 86, 140 100, 160 128 C140 118, 122 114, 100 116 C78 114, 60 118, 40 128Z" fill="white" opacity="0.15"/>
                  {/* Eagle head 
                  <ellipse cx="100" cy="78" rx="18" ry="20" fill="#A5ACAF"/>
                  {/* Beak 
                  <path d="M115 82 L128 88 L115 90Z" fill="#ACC0A0" opacity="0.9"/>
                  {/* Eye 
                  <circle cx="110" cy="76" r="4" fill="#4e69b2"/>
                  <circle cx="111" cy="75" r="1.5" fill="white"/>
                  {/* Body 
                  <ellipse cx="100" cy="130" rx="28" ry="22" fill="#4e69b2" stroke="#A5ACAF" strokeWidth="1"/>
                  {/* Stars decoration 
                  <text x="100" y="158" textAnchor="middle" fontSize="8" fill="#A5ACAF" fontWeight="bold" letterSpacing="3">★ ★ ★</text>
                  {/* Text arc 
                  <path id="topArc" d="M 28 100 A 72 72 0 0 1 172 100" fill="none"/>
                  <text fontSize="10" fontWeight="700" fill="#A5ACAF" letterSpacing="2.5">
                    <textPath href="#topArc" startOffset="10%">PHILADELPHIA EAGLES</textPath>
                  </text>
                </svg>
              </div>*/}
              <img src='/images/eagles-logo.png' alt="Eagles"></img>
              <div className={s.eaglesPartnerBadge}>
                <span className={s.eaglesBadgeIcon}>🦅</span>
                <span className={s.eaglesBadgeText}>Official Dental Partner</span>
              </div>              
            </div>
            
            {/* Center: Main copy */}
            <div className={s.eaglesContent}>
              <p className={s.eaglesEyebrow}>
                <span className={s.eaglesEyebrowLine} />
                Proud Partner
                <span className={s.eaglesEyebrowLine} />
              </p>
              <h2 className={s.eaglesHeading}>
                Partnership with<br/>
                <em className={s.eaglesEm}>Team Eagles</em>
              </h2>
              <p className={s.eaglesBody}>
                Celebrity Smile Studio is proud to be a trusted dental partner of the Philadelphia Eagles. 
                As a Fly Eagles Fly community, we share the same commitment to excellence, 
                performance, and team spirit — on the field and in the dental chair.
              </p>
              <p className={s.eaglesBody} style={{marginTop:'12px'}}>
                Our practice serves Eagles players, staff, and the broader Flourtown and 
                greater Philadelphia community. When you visit us, you're getting the same 
                level of care trusted by champions.
              </p>

              <div className={s.eaglesPillars}>
                {[
                  { icon: '🏆', label: 'Champions\' Care' },
                  { icon: '💪', label: 'Peak Performance' },
                  { icon: '🦅', label: 'Fly Eagles Fly' },
                  { icon: '🌟', label: 'Excellence Always' },
                ].map((p, i) => (
                  <div key={i} className={s.eaglesPillar}>
                    <span className={s.eaglesPillarIcon}>{p.icon}</span>
                    <span className={s.eaglesPillarLabel}>{p.label}</span>
                  </div>
                ))}
              </div>

              <div className={s.eaglesActions}>
                <a href="https://www.philadelphiaeagles.com/" target='_blank' className={s.eaglesCta}>
                  Read More
                  <span className={s.eaglesCtaArrow}>→</span>
                </a>
                <a href={`tel:${PRACTICE_INFO.phoneRaw}`} className={s.eaglesPhone}>
                  📞 {PRACTICE_INFO.phone}
                </a>
              </div>
            </div>

            {/* Right: Stats cards */}
            <div className={s.eaglesStats}>
              <div className={s.eaglesStatCard}>
                <div className={s.eaglesStatNum}>30<span>+</span></div>
                <div className={s.eaglesStatLabel}>Years Combined<br/>Dental Excellence</div>
              </div>
              <div className={s.eaglesStatCard}>
                <div className={s.eaglesStatNum}>5<span>★</span></div>
                <div className={s.eaglesStatLabel}>619 Google<br/>Reviews</div>
              </div>
              <div className={s.eaglesStatCard + ' ' + s.eaglesStatCardAccent}>
                <div className={s.eaglesStatIcon}>🦅</div>
                <div className={s.eaglesStatLabel}>Proud Eagles<br/>Community Partner</div>
              </div>
              <div className={s.eaglesJersey}>
                <div className={s.eaglesJerseyNum}>1</div>
                <div className={s.eaglesJerseyLabel}>Choice for<br/>Philadelphia Smiles</div>
              </div>
              </div>
            </div>
          </div>
          </section>


      {/* ── Location Section ───────────────────────────────────── */}
      <section className={`section ${s.locationSection}`}>
        <div className="container">

          <div className={`${s.locationHeader} reveal`}>
            <p className="eyebrow" style={{justifyContent:'center'}}>Find Us</p>
            <h2 className="display-heading" style={{textAlign:'center',marginBottom:'12px'}}>
              Visit Our <em>Office</em>
            </h2>
            <p className={s.locationHeaderSub}>
              Conveniently located in Flourtown, PA — serving Chestnut Hill, Fort Washington, Ambler, and surrounding communities.
            </p>
          </div>

          {/* Map + Info grid */}
          <div className={`${s.locGrid} reveal`}>

            {/* Left: Address, hours, phone, CTA */}
            <div className={s.locInfoPanel}>
              <div className={s.locAddrBlock}>
                <div className={s.locAddrIcon}>📍</div>
                <div>
                  <div className={s.locAddrLine}>1811 Bethlehem Pike, Suite B235</div>
                  <div className={s.locAddrLine}>Flourtown, PA 19031</div>
                  <a
                    href={PRACTICE_INFO.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.locDirLink}
                  >
                    Get Directions →
                  </a>
                </div>
              </div>

              <div className={s.locDivider} />

              <h4 className={s.locHoursTitle}>Office Hours</h4>
              <div className={s.locHoursTable}>
                {OFFICE_HOURS.map((h, i) => (
                  <div key={i} className={`${s.locHoursRow}${h.closed ? ' ' + s.locClosed : ''}`}>
                    <span className={s.locHoursDay}>{h.day}</span>
                    <span className={s.locHoursTime}>{h.hours}</span>
                  </div>
                ))}
              </div>

              <div className={s.locDivider} />

              <div className={s.locPhoneRow}>
                <div className={s.locPhoneIcon}>📞</div>
                <div>
                  <div className={s.locPhoneLabel}>Call Us</div>
                  <a href={`tel:${PRACTICE_INFO.phoneRaw}`} className={s.locPhoneNum}>
                    {PRACTICE_INFO.phone}
                  </a>
                </div>
              </div>

              <Link to="/book-appointment" className={`btn btn-primary ${s.locBookBtn}`}>
                📅 Book Appointment
              </Link>

              <Link to="/contact/location" className={s.locFullLink}>
                View full location &amp; directions →
              </Link>
            </div>

            {/* Right: Google Map */}
            <div className={s.locMapWrap}>
              <div className={s.locMapFrame}>
                <iframe
                  title="Celebrity Smile Studio Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3051.0!2d-75.2089!3d40.1066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6860b7af4e3f7%3A0xf13e7a30817c7fa6!2sCelebrity%20Smiles!5e0!3m2!1sen!2sus!4v1709500000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className={s.locMapActions}>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Celebrity+Smiles+1811+Bethlehem+Pike+Flourtown+PA+19031"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.locMapBtn}
                >
                  🗺️ Open in Google Maps
                </a>
                <a
                  href="https://maps.apple.com/?daddr=1811+Bethlehem+Pike+Flourtown+PA+19031"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.locMapBtn}
                >
                  🍎 Open in Apple Maps
                </a>
              </div>
            </div>

          </div>

          {/* Nearby info cards */}
          <div className={s.locNearbyGrid}>
            {[
              { icon: '🅿️', label: 'Parking',          text: 'Free parking lot directly in front of the building. Ample spaces available.' },
              { icon: '🚌', label: 'SEPTA Bus',         text: 'Route 94 stops at Bethlehem Pike & Norristown Rd, a 3-minute walk.' },
              { icon: '♿', label: 'Accessibility',     text: 'Elevator access to Suite B235. Wheelchair accessible throughout.' },
              { icon: '📍', label: 'Nearby Landmarks',  text: 'Located in Fort Washington Marketplace, across from Giant supermarket.' },
            ].map((item, i) => (
              <div key={i} className={`${s.locNearbyCard} reveal`} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className={s.locNearbyIcon}>{item.icon}</div>
                <div className={s.locNearbyLabel}>{item.label}</div>
                <p className={s.locNearbyText}>{item.text}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      <TestimonialsSection />

      {/* Book Section */}
      <section className={`section ${s.bookSection}`} id="book">
        <div className="container">
          <div className={s.bookGrid}>
            <div className={`reveal`}>
              <p className="eyebrow">Book Now</p>
              <h2 className="display-heading" style={{marginBottom:'16px'}}>Schedule Your<br/><em>Visit Today</em></h2>
              <p style={{fontSize:'15px',fontWeight:'300',color:'var(--slate-600)',lineHeight:'1.75',marginBottom:'28px'}}>We welcome new patients and are accepting appointments for all services. Fill out the form or give us a call — our team will confirm within one business day.</p>

              {[
                { icon: '📞', label: 'Phone', value: <a href={`tel:${PRACTICE_INFO.phoneRaw}`}>{PRACTICE_INFO.phone}</a> },
                { icon: '📍', label: 'Address', value: PRACTICE_INFO.fullAddress },
              ].map((item, i) => (
                <div key={i} className={s.infoRow}>
                  <div className={s.infoIcon}>{item.icon}</div>
                  <div><div className={s.infoLabel}>{item.label}</div><div className={s.infoValue}>{item.value}</div></div>
                </div>
              ))}

              <h4 className={s.hoursTitle}>Office Hours</h4>
              <div className={s.hoursGrid}>
                {OFFICE_HOURS.map((h, i) => (
                  <div key={i} className={s.hourItem}>
                    <div className={s.hourDay}>{h.day}</div>
                    <div className={`${s.hourTime} ${h.closed ? s.closed : ''}`}>{h.hours}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className={`${s.formWrap} reveal reveal-delay-2`}>
              <AppointmentForm />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
